'use client';

import { useEffect, useState } from 'react';

interface PromotionalBannerConfig {
  enabled: boolean;
  text: string;
  endDate: string;
  backgroundImage?: string;
  textColor?: string;
}

interface TimeLeft {
  hours: number;
  minutes: number;
  seconds: number;
}

export function PromotionalBanner() {
  const [config, setConfig] = useState<PromotionalBannerConfig | null>(null);
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({ hours: 0, minutes: 0, seconds: 0 });
  const [isLoading, setIsLoading] = useState(true);

  // Fetch banner configuration
  useEffect(() => {
    async function fetchConfig() {
      try {
        const response = await fetch('/api/promotional-banner');
        const data = await response.json();
        
        if (data.success && data.config) {
          setConfig(data.config);
        } else {
          console.log('[PromoBanner] No configuration available:', data.message || 'Unknown reason');
          setConfig(null);
        }
      } catch (error) {
        console.error('[PromoBanner] Failed to fetch configuration:', error);
        setConfig(null);
      } finally {
        setIsLoading(false);
      }
    }

    fetchConfig();
  }, []);

  // Update countdown timer
  useEffect(() => {
    if (!config?.endDate) return;

    function calculateTimeLeft(): TimeLeft {
      const now = new Date().getTime();
      const endTime = new Date(config!.endDate).getTime();
      const difference = endTime - now;

      if (difference > 0) {
        return {
          hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((difference % (1000 * 60)) / 1000)
        };
      }

      return { hours: 0, minutes: 0, seconds: 0 };
    }

    // Update immediately
    setTimeLeft(calculateTimeLeft());

    // Set up interval to update every second
    const timer = setInterval(() => {
      const newTimeLeft = calculateTimeLeft();
      setTimeLeft(newTimeLeft);

      // Hide banner when countdown reaches zero
      if (newTimeLeft.hours === 0 && newTimeLeft.minutes === 0 && newTimeLeft.seconds === 0) {
        setConfig(null);
        clearInterval(timer);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [config?.endDate]);

  // Don't render if loading, no config, or banner is disabled
  if (isLoading || !config || !config.enabled) {
    return null;
  }

  return (
    <div 
      className="w-full relative"
      data-component="promotional-banner"
      style={{
        minHeight: 'clamp(32px, 8vw, 86px)', // More aggressive scaling from 60px to 32px
        height: 'auto',
        backgroundColor: '#FEFFFA',
        backgroundImage: config.backgroundImage ? `url(${config.backgroundImage})` : undefined,
        backgroundSize: config.backgroundImage ? 'auto 100%' : 'auto',
        backgroundPosition: config.backgroundImage ? '100% center' : 'center',
        backgroundRepeat: 'no-repeat',
        zIndex: 'var(--z-promotional-banner)'
      }}
    >
      <div 
        className="relative w-full mx-auto flex flex-col justify-center items-center px-2 py-1 sm:px-4 sm:py-2 lg:px-[43px] lg:py-3"
        style={{
          minHeight: 'clamp(32px, 8vw, 86px)', // More aggressive scaling from 60px to 32px
          height: 'auto',
          maxWidth: '1280px'
        }}
      >
        {/* Main content container - mobile: left-aligned, desktop: centered */}
        <div className="flex flex-row items-center justify-start lg:justify-start w-full gap-2 sm:gap-4 lg:gap-[38px] lg:max-w-[1250px] relative z-10">
          {/* Text section - mobile: left-aligned, desktop: right-aligned */}
          <div className="flex flex-col items-start lg:items-end w-auto lg:w-auto text-left lg:text-right">
            {/* Promotional text */}
            <div
              className="w-auto max-w-[90vw] xs:max-w-[75vw] sm:max-w-[45vw] lg:max-w-[495px]"
              style={{
                // For extra small screens, force max-width to 90vw
                ...(typeof window !== 'undefined' && window.innerWidth < 400 ? { maxWidth: '90vw' } : {}),
                fontFamily: "'Futura Hv BT', 'Futura', 'Arial Black', sans-serif",
                fontWeight: 400,
                fontSize: 'clamp(8px, 2.2vw, 24px)', // More aggressive scaling from 10px to 8px
                lineHeight: '1.1',
                color: config.textColor || '#CC0000',
                wordWrap: 'break-word',
                wordBreak: 'break-word',
                hyphens: 'auto',
                overflowWrap: 'break-word'
              }}
            >
              {config.text}
            </div>
            {/* "Ends in" text */}
            <div
              className="mt-0.5"
              style={{
                fontFamily: "'Futura Md BT', 'Futura', 'Arial', sans-serif",
                fontWeight: 400,
                fontSize: 'clamp(6px, 1.6vw, 20px)', // More aggressive scaling from 8px to 6px
                lineHeight: '1.2',
                color: '#73777F'
              }}
            >
              Ends in
            </div>
          </div>
          {/* Timer section - consistent smaller sizing */}
          <div className="flex flex-row justify-center items-center gap-0.5 sm:gap-1 lg:gap-4">
            {/* Hours */}
            <div className="flex flex-col items-center gap-0.5">
              {/* Hours counter box */}
              <div 
                className="flex items-center justify-center rounded-[2px] sm:rounded-[3px] lg:rounded-[4px]"
                style={{
                  width: 'clamp(14px, 6vw, 46px)', // More aggressive scaling from 18px to 14px
                  height: 'clamp(11px, 4.5vw, 37px)', // More aggressive scaling from 14px to 11px
                  padding: 'clamp(0.5px, 0.3vw, 4px) clamp(1px, 0.6vw, 8px)',
                  background: 'linear-gradient(90deg, #14BBD3 0.22%, #263AEA 100.43%)'
                }}
              >
                <div
                  className="flex items-center justify-center"
                  style={{
                    fontFamily: "'Futura Hv BT', 'Futura', 'Arial Black', sans-serif",
                    fontWeight: 400,
                    fontSize: 'clamp(6px, 2.4vw, 24px)', // More aggressive scaling from 8px to 6px
                    lineHeight: '1',
                    color: '#FFFFFF'
                  }}
                >
                  {timeLeft.hours.toString().padStart(2, '0')}
                </div>
              </div>
              {/* Hours label */}
              <div
                className="text-center whitespace-nowrap"
                style={{
                  fontFamily: "'Futura Hv BT', 'Futura', 'Arial Black', sans-serif",
                  fontWeight: 400,
                  fontSize: 'clamp(4px, 1.4vw, 14px)', // More aggressive scaling from 6px to 4px
                  lineHeight: '1.1',
                  color: '#73777F'
                }}
              >
                Hours
              </div>
            </div>
            {/* Separator : */}
            <div
              className="flex items-center justify-center self-start mt-0.5"
              style={{
                fontFamily: "'Futura Hv BT', 'Futura', 'Arial Black', sans-serif",
                fontWeight: 400,
                fontSize: 'clamp(8px, 3vw, 30px)', // More aggressive scaling from 12px to 8px
                lineHeight: '1',
                color: '#000000'
              }}
            >
              :
            </div>
            {/* Minutes */}
            <div className="flex flex-col items-center gap-0.5">
              {/* Minutes counter box */}
              <div 
                className="flex items-center justify-center rounded-[2px] sm:rounded-[3px] lg:rounded-[4px]"
                style={{
                  width: 'clamp(14px, 6vw, 46px)',
                  height: 'clamp(11px, 4.5vw, 37px)',
                  padding: 'clamp(0.5px, 0.3vw, 4px) clamp(1px, 0.6vw, 8px)',
                  background: 'linear-gradient(90deg, #14BBD3 0.22%, #263AEA 100.43%)'
                }}
              >
                <div
                  className="flex items-center justify-center"
                  style={{
                    fontFamily: "'Futura Hv BT', 'Futura', 'Arial Black', sans-serif",
                    fontWeight: 400,
                    fontSize: 'clamp(6px, 2.4vw, 24px)',
                    lineHeight: '1',
                    color: '#FFFFFF'
                  }}
                >
                  {timeLeft.minutes.toString().padStart(2, '0')}
                </div>
              </div>
              {/* Minutes label */}
              <div
                className="text-center whitespace-nowrap"
                style={{
                  fontFamily: "'Futura Hv BT', 'Futura', 'Arial Black', sans-serif",
                  fontWeight: 400,
                  fontSize: 'clamp(4px, 1.4vw, 14px)',
                  lineHeight: '1.1',
                  color: '#73777F'
                }}
              >
                Minutes
              </div>
            </div>
            {/* Separator : */}
            <div
              className="flex items-center justify-center self-start mt-0.5"
              style={{
                fontFamily: "'Futura Hv BT', 'Futura', 'Arial Black', sans-serif",
                fontWeight: 400,
                fontSize: 'clamp(8px, 3vw, 30px)',
                lineHeight: '1',
                color: '#000000'
              }}
            >
              :
            </div>
            {/* Seconds */}
            <div className="flex flex-col items-center gap-0.5">
              {/* Seconds counter box */}
              <div 
                className="flex items-center justify-center rounded-[2px] sm:rounded-[3px] lg:rounded-[4px]"
                style={{
                  width: 'clamp(14px, 6vw, 46px)',
                  height: 'clamp(11px, 4.5vw, 37px)',
                  padding: 'clamp(0.5px, 0.3vw, 4px) clamp(1px, 0.6vw, 8px)',
                  background: 'linear-gradient(90deg, #14BBD3 0.22%, #263AEA 100.43%)'
                }}
              >
                <div
                  className="flex items-center justify-center"
                  style={{
                    fontFamily: "'Futura Hv BT', 'Futura', 'Arial Black', sans-serif",
                    fontWeight: 400,
                    fontSize: 'clamp(6px, 2.4vw, 24px)',
                    lineHeight: '1',
                    color: '#FFFFFF'
                  }}
                >
                  {timeLeft.seconds.toString().padStart(2, '0')}
                </div>
              </div>
              {/* Seconds label */}
              <div
                className="text-center whitespace-nowrap"
                style={{
                  fontFamily: "'Futura Hv BT', 'Futura', 'Arial Black', sans-serif",
                  fontWeight: 400,
                  fontSize: 'clamp(4px, 1.4vw, 14px)',
                  lineHeight: '1.1',
                  color: '#73777F'
                }}
              >
                Seconds
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 